import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { API_URL as API_BASE } from "../config";
import QuoteEstimatePanel from "./quotes/QuoteEstimatePanel";
import QuoteScopeSections from "./quotes/QuoteScopeSections";
import QuotePlannerPanel from "./quotes/QuotePlannerPanel";
import {
    DEFAULT_SECTION_FLAGS,
    parseSectionFlags,
    serializeSectionFlags,
} from "./quotes/quoteSectionConfig";
import { buildProjectTree, normalizePlannerGroups } from "./quotes/plannerConfig";
import { groupQuoteOwn } from "./quotes/plannerQuoteRates";
import { estimateLinesFromWixQuote, customerQuotePresentationFromComponents, formatCustomerQuotePackagesText, internalQuoteEconomicsFromComponents } from "./quotes/wixJobQuote";
import { computeQuoteTotals, formatMoney, lineExtension, newEstimateLineId } from "./quotes/quotePricingMath";
import { exportCustomerQuotePdf, exportInternalQuotePdf, buildCustomerPdfDetails } from "./quotes/quotePdf";

const QUOTES_API = `${API_BASE}/job-quotes`;
const CUSTOMERS_API = `${API_BASE}/customers`;
const PROJECTS_API = `${API_BASE}/projects`;
const PRICING_API = `${API_BASE}/pricing`;
const PLANNER_DEFS_API = `${API_BASE}/planner/group-definitions`;

const STATUS_OPTIONS = [
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "follow_up", label: "Follow up" },
    { value: "won", label: "Won" },
    { value: "lost", label: "Lost" },
];

const emptyForm = () => ({
    title: "",
    customer_id: "",
    project_id: "",
    status: "draft",
    shop_type: "",
    machining_cam_consult: "",
    robot_implementation: "",
    estimated_time: "",
    travel: "",
    materials_needed: "",
    customer_material_actions: "",
    scheduling_planning: "",
    software_requirements: "",
    customer_equipment: "",
    other_notes: "",
    material_cost_at_purchase: 0,
    customer_quote_override: "",
});

function buildSummary(payload) {
    const lines = [
        "JOB QUOTE WORKSHEET",
        "=".repeat(40),
        `Title / job: ${payload.title || "(untitled)"}`,
    ];
    if (payload.customer_name) lines.push(`Customer: ${payload.customer_name}`);
    if (payload.status) lines.push(`Status: ${payload.status}`);
    lines.push("");

    const blocks = [
        ["Shop type / environment", payload.shop_type],
        ["Machining / CAM consulting", payload.machining_cam_consult],
        ["Robot implementation / integration scope", payload.robot_implementation],
        ["Time scope (engineering, install, runoff, etc.)", payload.estimated_time],
        ["Travel (distance, lodging, onsite days)", payload.travel],
        ["Materials needed (consumables, hardware, purchased parts)", payload.materials_needed],
        ["Customer-supplied materials & actions", payload.customer_material_actions],
        ["Scheduling & planning milestones", payload.scheduling_planning],
        ["Software / licensing / CAM / PLC / OEM stack", payload.software_requirements],
        ["Customer machines & facility equipment", payload.customer_equipment],
        ["Other notes / exclusions / assumptions", payload.other_notes],
    ];

    for (const [label, body] of blocks) {
        const t = (body && String(body).trim()) || "";
        if (!t) continue;
        lines.push(`${label}`, "-".repeat(Math.min(label.length + 4, 50)), t, "");
    }

    lines.push("(End worksheet)");
    return lines.join("\n");
}

function WorksheetTabPanel({ value, index, children }) {
    if (value !== index) return null;
    return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function coerceMoneyNumber(value) {
    const n = Number(value);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, n);
}

function normalizeEstimateLinePayload(L, idx) {
    const qty = coerceMoneyNumber(L.qty);
    const bill_rate = coerceMoneyNumber(L.bill_rate);
    return {
        id: L.id ? String(L.id) : undefined,
        label: L.label != null ? String(L.label) : "",
        rate_key: L.rate_key != null ? String(L.rate_key) : null,
        qty,
        unit: String(L.unit || "hour"),
        bill_rate,
        sort_order: idx,
        group_id: L.group_id ? String(L.group_id) : null,
    };
}

function buildCustomerBallparkClipboard({ title, customerName, totals, currency, verbal, packages, customerItems }) {
    const cur = currency || "USD";
    const lines = [
        `Project: ${title || "(untitled)"}`,
        customerName ? `Customer: ${customerName}` : null,
        "",
        `Point Blank Robotics total: ${formatMoney(totals.customer_total_rounded, cur)}`,
        "",
    ];
    const packageText = formatCustomerQuotePackagesText(packages, cur);
    if (packageText) {
        lines.push(packageText, "");
    }
    if (customerItems?.length) {
        lines.push("Customer-supplied equipment");
        for (const item of customerItems) {
            lines.push(`${item.title} — ${formatMoney(item.amount, cur)}`);
        }
        lines.push("");
    }
    const v = coerceMoneyNumber(verbal);
    if (v > 0) {
        lines.push(`Verbal anchor: ${formatMoney(v, cur)}`);
        lines.push("");
    }
    return lines.filter((line) => line != null).join("\n").trim();
}

function buildCostDetailClipboard({
    title,
    customerName,
    estimateLines,
    totals,
    currency,
    economics,
}) {
    const cur = currency || "USD";
    const lines = [
        "DETAILED BALLPARK ROLLUP",
        "=".repeat(48),
        `Project: ${title || "(untitled)"}`,
        customerName ? `Customer: ${customerName}` : null,
        "",
        "LINE BUILDER",
        "-".repeat(48),
    ];
    if (!estimateLines.length) {
        lines.push("(no estimator lines)");
    } else {
        estimateLines.forEach((L, i) => {
            const qty = coerceMoneyNumber(L.qty);
            const rate = coerceMoneyNumber(L.bill_rate);
            const bucket = L.rate_key ? `bucket:${L.rate_key}` : "custom";
            lines.push(`${i + 1}. ${L.label || "(line)"}`);
            lines.push(`   • ${qty} ${L.unit} × ${formatMoney(rate, cur)} ⇒ ${formatMoney(lineExtension(L), cur)} (${bucket})`);
        });
    }
    lines.push(
        "",
        "ECONOMICS",
        "-".repeat(48),
        `Services subtotal .......... ${formatMoney(totals.services_subtotal, cur)}`,
        `Material cost basis ........ ${formatMoney(totals.materials_cost_basis, cur)}`,
        `Materials w/ markup ........ ${formatMoney(totals.materials_customer, cur)} (${totals.materials_markup_percent}%)`,
        `Pre-risk subtotal .......... ${formatMoney(totals.pre_risk_subtotal, cur)}`,
        `Contingency (${totals.contingency_percent}%) ... ${formatMoney(totals.contingency_amount, cur)}`,
        totals.tax_percent > 0 ? `Tax (${totals.tax_percent}%) ................. ${formatMoney(totals.tax_amount, cur)}` : null,
        `Rounded customer total ..... ${formatMoney(totals.customer_total_rounded, cur)}`,
        `Exact modeled total ........ ${formatMoney(totals.customer_total_exact, cur)}`,
    );
    if (economics && (economics.trueCost || economics.revenue)) {
        lines.push(
            "",
            "INTERNAL MARGIN",
            "-".repeat(48),
            `Customer charge ............ ${formatMoney(economics.revenue, cur)}`,
            `Out of pocket .............. ${formatMoney(economics.trueCost, cur)}`,
            `Gross profit ............... ${formatMoney(economics.grossProfit, cur)}`,
            `Margin ..................... ${Number(economics.marginPct).toFixed(1)}%`,
            `Markup on cost ............. ${Number(economics.markupOnCostPct).toFixed(1)}%`,
            `Material markup ............ ${formatMoney(economics.materialMarkupAmount, cur)} (${Number(economics.materialMarkupPercent).toFixed(1)}%)`,
            `Internal-only absorbed ..... ${formatMoney(economics.absorbedInternal, cur)}`,
            `  Internal labor ........... ${formatMoney(economics.internalLaborCost, cur)}`,
            `  Internal PBR purchases ... ${formatMoney(economics.internalPurchaseCost, cur)}`,
            `Hours logged ............... ${Number(economics.actualHours || 0)} vs ${Number(economics.laborHours || 0)} quoted`,
            `Extra materials ............ ${formatMoney(economics.extraMaterialCost, cur)}`,
        );
    }
    lines.push("", "(End rollup)");
    return lines.filter(Boolean).join("\n");
}

const Quotes = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [quotes, setQuotes] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [listQuery, setListQuery] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [sectionFlags, setSectionFlags] = useState(() => ({ ...DEFAULT_SECTION_FLAGS }));
    const [worksheetTab, setWorksheetTab] = useState(0);
    const [estimateLines, setEstimateLines] = useState([]);
    const [plannerGroups, setPlannerGroups] = useState([]);
    const [wixComponents, setWixComponents] = useState([]);
    const [wixProjectName, setWixProjectName] = useState("");
    const [wixJob, setWixJob] = useState(null);
    const [groupDefinitions, setGroupDefinitions] = useState([]);
    const [projects, setProjects] = useState([]);
    const [projectFilterId, setProjectFilterId] = useState("");
    const [estimateGroupFilter, setEstimateGroupFilter] = useState(null);
    const [pricingModel, setPricingModel] = useState(null);
    const [pricingLoading, setPricingLoading] = useState(true);
    const [pdfExporting, setPdfExporting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteProjectOpen, setDeleteProjectOpen] = useState(false);
    const [projectDialogOpen, setProjectDialogOpen] = useState(false);
    const [newProjectForm, setNewProjectForm] = useState({
        name: "",
        customer_id: "",
        parent_id: "",
        description: "",
        site_location: "",
    });

    const customerNameForForm = useMemo(() => {
        if (!form.customer_id) return "";
        const c = customers.find((x) => String(x.id) === String(form.customer_id));
        return c ? c.name : "";
    }, [customers, form.customer_id]);

    const projectNameForForm = useMemo(() => {
        if (!form.project_id) return "";
        const p = projects.find((x) => x.id === form.project_id);
        return p ? p.name : "";
    }, [projects, form.project_id]);

    const customerProjectsFlat = useMemo(() => {
        const forCustomer = projects.filter((p) => p.customer_id === form.customer_id);
        const flatten = (nodes, depth = 0) => {
            const out = [];
            nodes.forEach((n) => {
                out.push({ ...n, depth });
                if (n.children?.length) out.push(...flatten(n.children, depth + 1));
            });
            return out;
        };
        return flatten(buildProjectTree(forCustomer));
    }, [projects, form.customer_id]);

    const pricingSettings = useMemo(() => pricingModel?.settings || {}, [pricingModel]);

    const fetchQuotes = useCallback(async (qParam, projectId) => {
        try {
            const params = {};
            if (qParam) params.q = qParam;
            if (projectId) params.project_id = projectId;
            const res = await axios.get(QUOTES_API, { params });
            setQuotes(Array.isArray(res.data) ? res.data : []);
        } catch {
            setSnackbar({ open: true, message: "Failed to load worksheets.", severity: "error" });
        }
    }, []);

    const fetchCustomers = useCallback(async () => {
        try {
            const res = await axios.get(CUSTOMERS_API);
            setCustomers(Array.isArray(res.data) ? res.data : []);
        } catch {
            setSnackbar({ open: true, message: "Failed to load customers for linking.", severity: "warning" });
        }
    }, []);

    const fetchProjects = useCallback(async (customerId) => {
        try {
            const params = customerId ? { customer_id: customerId } : {};
            const res = await axios.get(PROJECTS_API, { params });
            setProjects(Array.isArray(res.data) ? res.data : []);
        } catch {
            setSnackbar({ open: true, message: "Failed to load projects.", severity: "warning" });
        }
    }, []);

    const fetchGroupDefinitions = useCallback(async () => {
        try {
            const res = await axios.get(PLANNER_DEFS_API);
            setGroupDefinitions(Array.isArray(res.data) ? res.data : []);
        } catch {
            setGroupDefinitions([]);
        }
    }, []);

    const fetchPricingModel = useCallback(async () => {
        setPricingLoading(true);
        try {
            const res = await axios.get(PRICING_API);
            setPricingModel(res.data);
        } catch {
            setSnackbar({ open: true, message: "Failed to load pricing model.", severity: "error" });
            setPricingModel(null);
        } finally {
            setPricingLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchQuotes();
        fetchCustomers();
        fetchProjects();
        fetchPricingModel();
        fetchGroupDefinitions();
    }, [fetchQuotes, fetchCustomers, fetchProjects, fetchPricingModel, fetchGroupDefinitions]);

    useEffect(() => {
        const t = setTimeout(() => {
            fetchQuotes(listQuery.trim() || undefined, projectFilterId || undefined);
        }, 300);
        return () => clearTimeout(t);
    }, [listQuery, projectFilterId, fetchQuotes]);

    useEffect(() => {
        if (form.customer_id) fetchProjects(form.customer_id);
    }, [form.customer_id, fetchProjects]);

    const hydrateFromQuoteDto = useCallback((dto) => {
        const row = dto || {};
        setSelectedId(row.id || null);
        const nextForm = {
            title: row.title || "",
            customer_id: row.customer_id || "",
            project_id: row.project_id || "",
            status: row.status || "draft",
            shop_type: row.shop_type || "",
            machining_cam_consult: row.machining_cam_consult || "",
            robot_implementation: row.robot_implementation || "",
            estimated_time: row.estimated_time || "",
            travel: row.travel || "",
            materials_needed: row.materials_needed || "",
            customer_material_actions: row.customer_material_actions || "",
            scheduling_planning: row.scheduling_planning || "",
            software_requirements: row.software_requirements || "",
            customer_equipment: row.customer_equipment || "",
            other_notes: row.other_notes || "",
            material_cost_at_purchase:
                row.material_cost_at_purchase != null ? coerceMoneyNumber(row.material_cost_at_purchase) : 0,
            customer_quote_override:
                row.customer_quote_override != null && Number.isFinite(Number(row.customer_quote_override))
                    ? String(row.customer_quote_override)
                    : "",
        };
        setForm(nextForm);
        setSectionFlags(parseSectionFlags(row.enabled_sections, nextForm));

        const lines = Array.isArray(row.estimate_lines) ? row.estimate_lines : [];
        setEstimateLines(
            lines.map((L) => ({
                id: L.id,
                label: L.label ?? "",
                rate_key: L.rate_key || null,
                qty: Number.isFinite(Number(L.qty)) ? Number(L.qty) : 0,
                unit: String(L.unit || "hour"),
                bill_rate: Number.isFinite(Number(L.bill_rate)) ? Number(L.bill_rate) : 0,
                group_id: L.group_id || null,
            }))
        );
        setPlannerGroups(normalizePlannerGroups(row.planner_groups));
        setWixComponents([]);
        setWixProjectName("");
        setWixJob(null);
    }, []);

    const handleSelectQuote = async (summaryRow) => {
        try {
            const res = await axios.get(`${QUOTES_API}/${summaryRow.id}`);
            hydrateFromQuoteDto(res.data);
        } catch {
            setSnackbar({ open: true, message: "Failed to load worksheet details.", severity: "error" });
        }
    };

    const handleNew = () => {
        setSelectedId(null);
        setEstimateLines([]);
        setPlannerGroups([]);
        setWixComponents([]);
        setWixProjectName("");
        setWixJob(null);
        setSectionFlags({ ...DEFAULT_SECTION_FLAGS });
        setWorksheetTab(0);
        setForm(emptyForm());
    };

    const applyWixJobQuote = useCallback(
        (built) => {
            if (!built?.job) return;
            const job = built.job;
            const email = String(job.customerEmail || "").trim().toLowerCase();
            const name = String(job.customerName || "").trim().toLowerCase();
            const match = (customers || []).find((c) => {
                const cEmail = String(c.email || "").trim().toLowerCase();
                const cName = String(c.company || c.name || "").trim().toLowerCase();
                return (email && cEmail === email) || (name && cName === name);
            });
            setSelectedId(null);
            setPlannerGroups([]);
            setWixComponents(built.components || []);
            setWixProjectName(built.selectedItem?.title || built.job.selectedItemTitle || built.job.title || "");
            setWixJob(built.sourceJob || built.job || null);
            setSectionFlags({ ...DEFAULT_SECTION_FLAGS });
            const estimateLines = estimateLinesFromWixQuote(built);
            const presentation = customerQuotePresentationFromComponents(built.components || []);
            const packageNotes = formatCustomerQuotePackagesText(presentation.packages);
            const customerPurchaseNotes = presentation.customerItems
                .map((item) => `${item.title}${item.amount ? ` — ${formatMoney(item.amount)}` : ""}`)
                .join("\n");
            setEstimateLines(estimateLines);
            setForm({
                ...emptyForm(),
                title: job.title || "",
                customer_id: match?.id || "",
                robot_implementation: packageNotes || `Built from planner: ${job.title || "project"}`,
                materials_needed: customerPurchaseNotes,
                material_cost_at_purchase: 0,
            });
            setWorksheetTab(2);
            setSnackbar({
                open: true,
                message: presentation.packages.length
                    ? `Loaded ${presentation.packages.length} customer work package${presentation.packages.length === 1 ? "" : "s"} from “${job.title}”.`
                    : `Loaded quote from “${job.title}”.`,
                severity: "success",
            });
        },
        [customers]
    );

    useEffect(() => {
        const built = location.state?.jobQuote || location.state?.wixJobQuote;
        if (!built?.job) return;
        applyWixJobQuote(built);
        navigate("/quotes", { replace: true, state: {} });
    }, [location.state, applyWixJobQuote, navigate]);

    const handleDeleteProjectConfirmed = async () => {
        const projectId = form.project_id;
        if (!projectId) return;
        try {
            await axios.delete(`${PROJECTS_API}/${projectId}`);
            await fetchProjects(form.customer_id || undefined);
            if (projectFilterId === projectId) setProjectFilterId("");
            setForm((f) => ({ ...f, project_id: "" }));
            setDeleteProjectOpen(false);
            setSnackbar({ open: true, message: "Project removed.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e?.response?.data?.error || "Failed to delete project.", severity: "error" });
        }
    };

    const handleCreateProject = async () => {
        const name = newProjectForm.name.trim();
        const customer_id = newProjectForm.customer_id || form.customer_id;
        if (!name || !customer_id) {
            setSnackbar({ open: true, message: "Project name and customer are required.", severity: "warning" });
            return;
        }
        try {
            const parent_id = newProjectForm.parent_id || null;
            const res = await axios.post(PROJECTS_API, { ...newProjectForm, name, customer_id, parent_id });
            await fetchProjects(customer_id);
            setForm((f) => ({ ...f, customer_id, project_id: res.data.id }));
            setProjectDialogOpen(false);
            setNewProjectForm({ name: "", customer_id: "", parent_id: "", description: "", site_location: "" });
            setSnackbar({ open: true, message: "Project created.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e?.response?.data?.error || "Failed to create project.", severity: "error" });
        }
    };

    const handleAddEstimateLineForGroup = (group) => {
        const quote = groupQuoteOwn(group);
        setEstimateLines((rows) => [
            ...rows,
            {
                id: newEstimateLineId(),
                label: group.label ? `${group.label} — ${quote.profile.label}` : quote.profile.label,
                rate_key: null,
                qty: quote.laborHours || 8,
                unit: "hour",
                bill_rate: quote.profile.hourlyRate,
                group_id: group.id,
            },
        ]);
        setWorksheetTab(2);
        setEstimateGroupFilter(group.id);
    };

    const persistPayload = () => {
        const overrideRaw = form.customer_quote_override;
        let cq = null;
        if (overrideRaw !== "" && overrideRaw != null) {
            const n = Number(overrideRaw);
            cq = Number.isFinite(n) ? n : null;
        }

        return {
            title: form.title.trim(),
            customer_id: form.customer_id || null,
            project_id: form.project_id || null,
            status: form.status,
            shop_type: form.shop_type || null,
            machining_cam_consult: form.machining_cam_consult || null,
            robot_implementation: form.robot_implementation || null,
            estimated_time: form.estimated_time || null,
            travel: form.travel || null,
            materials_needed: form.materials_needed || null,
            customer_material_actions: form.customer_material_actions || null,
            scheduling_planning: form.scheduling_planning || null,
            software_requirements: form.software_requirements || null,
            customer_equipment: form.customer_equipment || null,
            other_notes: form.other_notes || null,
            material_cost_at_purchase: coerceMoneyNumber(form.material_cost_at_purchase),
            customer_quote_override: cq,
            enabled_sections: serializeSectionFlags(sectionFlags),
            estimate_lines: estimateLines.map(normalizeEstimateLinePayload),
            planner_groups: plannerGroups.map((g, gi) => ({
                id: g.id,
                parent_id: g.parent_id || null,
                definition_id: g.definition_id || null,
                group_key: g.group_key,
                label: g.label,
                scope_notes: g.scope_notes || null,
                sort_order: gi,
                enabled: g.enabled !== false,
                material_cost: Number(g.material_cost) || 0,
                labor_hours: Number(g.labor_hours) || 0,
                items: (g.items || [])
                    .filter((it) => String(it.title || "").trim())
                    .map((it, ii) => ({
                        id: it.id,
                        title: it.title.trim(),
                        body: it.body || null,
                        item_type: it.item_type || "idea",
                        status: it.status || "open",
                        priority: it.priority || "medium",
                        sort_order: ii,
                    })),
            })),
        };
    };

    const handleSave = async () => {
        if (!form.title.trim()) {
            setSnackbar({ open: true, message: "Add a short title before saving.", severity: "warning" });
            return;
        }
        const body = persistPayload();
        try {
            if (selectedId) {
                const res = await axios.put(`${QUOTES_API}/${selectedId}`, body);
                setSnackbar({ open: true, message: "Worksheet updated.", severity: "success" });
                hydrateFromQuoteDto(res.data);
            } else {
                const res = await axios.post(QUOTES_API, body);
                setSnackbar({ open: true, message: "Worksheet saved.", severity: "success" });
                hydrateFromQuoteDto(res.data);
            }
            fetchQuotes(listQuery.trim() || undefined, projectFilterId || undefined);
        } catch (e) {
            const msg = e?.response?.data?.error || "Save failed.";
            setSnackbar({ open: true, message: msg, severity: "error" });
        }
    };

    const handleDeleteConfirmed = async () => {
        if (!selectedId) return;
        try {
            await axios.delete(`${QUOTES_API}/${selectedId}`);
            setSnackbar({ open: true, message: "Worksheet deleted.", severity: "success" });
            handleNew();
            setDeleteOpen(false);
            fetchQuotes(listQuery.trim() || undefined, projectFilterId || undefined);
        } catch {
            setSnackbar({ open: true, message: "Delete failed.", severity: "error" });
        }
    };

    const handleCopy = async () => {
        const text = buildSummary({
            ...persistPayload(),
            customer_name: customerNameForForm,
            status: STATUS_OPTIONS.find((s) => s.value === form.status)?.label || form.status,
        });
        try {
            await navigator.clipboard.writeText(text);
            setSnackbar({ open: true, message: "Summary copied to clipboard.", severity: "success" });
        } catch {
            setSnackbar({
                open: true,
                message: "Clipboard not available — select text manually.",
                severity: "warning",
            });
        }
    };

    const handleExportInternalPdf = useCallback(async () => {
        setPdfExporting(true);
        try {
            await exportInternalQuotePdf({
                form,
                customerName: customerNameForForm,
                projectName: projectNameForForm || wixProjectName,
                estimateLines,
                plannerGroups,
                wixComponents,
                wixJob,
                pricingSettings,
                totals: computeQuoteTotals({
                    material_cost_at_purchase: coerceMoneyNumber(form.material_cost_at_purchase),
                    lines: estimateLines,
                    settings: pricingSettings,
                }),
            });
            setSnackbar({ open: true, message: "Internal PDF downloaded.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e?.message || "PDF export failed.", severity: "error" });
        } finally {
            setPdfExporting(false);
        }
    }, [
        customerNameForForm,
        estimateLines,
        form,
        plannerGroups,
        pricingSettings,
        projectNameForForm,
        wixComponents,
        wixJob,
        wixProjectName,
    ]);

    const handleExportCustomerPdf = useCallback(async () => {
        setPdfExporting(true);
        try {
            let selectedCustomer =
                customers.find((customer) => String(customer.id) === String(form.customer_id)) || null;
            if (!selectedCustomer && customerNameForForm) {
                selectedCustomer =
                    customers.find(
                        (customer) =>
                            String(customer.name || "").toLowerCase() === customerNameForForm.toLowerCase()
                    ) || null;
            }
            if (!selectedCustomer && wixJob?.customerEmail) {
                const email = String(wixJob.customerEmail).trim().toLowerCase();
                selectedCustomer =
                    customers.find(
                        (customer) => String(customer.email || "").trim().toLowerCase() === email
                    ) || null;
            }
            const customerDetails = buildCustomerPdfDetails({
                customer: selectedCustomer,
                customerName: customerNameForForm || wixJob?.customerName || "",
                contacts: [],
            });
            await exportCustomerQuotePdf({
                form,
                customerName: customerDetails.name || customerNameForForm || wixJob?.customerName || "",
                customerDetails,
                projectName: projectNameForForm || wixProjectName,
                estimateLines,
                wixComponents,
                pricingSettings,
                totals: computeQuoteTotals({
                    material_cost_at_purchase: coerceMoneyNumber(form.material_cost_at_purchase),
                    lines: estimateLines,
                    settings: pricingSettings,
                }),
            });
            setSnackbar({ open: true, message: "Customer PDF downloaded.", severity: "success" });
        } catch (e) {
            setSnackbar({ open: true, message: e?.message || "Customer PDF export failed.", severity: "error" });
        } finally {
            setPdfExporting(false);
        }
    }, [
        customerNameForForm,
        customers,
        estimateLines,
        form,
        pricingSettings,
        projectNameForForm,
        wixComponents,
        wixJob,
        wixProjectName,
    ]);

    const resolveTotals = (totalsPassed) =>
        totalsPassed ??
        computeQuoteTotals({
            material_cost_at_purchase: coerceMoneyNumber(form.material_cost_at_purchase),
            lines: estimateLines,
            settings: pricingSettings,
        });

    const copyWithToast = async (text, ok) => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbar({ open: true, message: ok, severity: "success" });
        } catch {
            setSnackbar({
                open: true,
                message: "Clipboard not available — select text manually.",
                severity: "warning",
            });
        }
    };

    const handleCopyCustomerBriefClipboard = async (totalsPassed) => {
        const totals = resolveTotals(totalsPassed);
        const presentation = customerQuotePresentationFromComponents(wixComponents);
        const txt = buildCustomerBallparkClipboard({
            title: form.title,
            customerName: customerNameForForm,
            totals,
            currency: pricingSettings.currency,
            verbal: form.customer_quote_override,
            packages: presentation.packages,
            customerItems: presentation.customerItems,
        });
        await copyWithToast(txt, "Customer brief copied.");
    };

    const handleCopyCostDetailClipboard = async (totalsPassed) => {
        const totals = resolveTotals(totalsPassed);
        const txt = buildCostDetailClipboard({
            title: form.title,
            customerName: customerNameForForm,
            estimateLines,
            totals,
            currency: pricingSettings.currency,
            economics: internalQuoteEconomicsFromComponents(wixComponents, {
                customerTotal: totals.customer_total_exact,
                actualHours: wixJob?.actualHours,
                extraMaterialCost: wixJob?.extraMaterialCost,
            }),
        });
        await copyWithToast(txt, "Cost detail copied.");
    };

    return (
        <>
            <Stack spacing={2} sx={{ pb: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "0.02em" }}>
                        Super quote studio
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 860 }}>
                        Tie estimates to customers and projects. Use the planner to capture needs by work package (robot cell,
                        software, palletizing…), then build scope and pricing on top.
                    </Typography>
                </Box>

                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }}>
                    <Paper
                        elevation={0}
                        variant="outlined"
                        sx={{
                            width: { xs: "100%", md: 300 },
                            flexShrink: 0,
                            display: "flex",
                            flexDirection: "column",
                            maxHeight: { md: "calc(100vh - 200px)" },
                        }}
                    >
                        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleNew}
                                    fullWidth
                                >
                                    New
                                </Button>
                            </Stack>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Search…"
                                value={listQuery}
                                onChange={(e) => setListQuery(e.target.value)}
                                sx={{ mt: 1.5 }}
                            />
                            <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                                <InputLabel id="project-filter-label">Filter project</InputLabel>
                                <Select
                                    labelId="project-filter-label"
                                    label="Filter project"
                                    value={projectFilterId}
                                    onChange={(e) => setProjectFilterId(e.target.value)}
                                >
                                    <MenuItem value="">All projects</MenuItem>
                                    {projects.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        <List dense sx={{ flex: 1, overflow: "auto", py: 0 }}>
                            {quotes.length === 0 ? (
                                <Box sx={{ p: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No worksheets yet. Start from &quot;New&quot; above.
                                    </Typography>
                                </Box>
                            ) : (
                                quotes.map((q) => {
                                    const sub = [q.project_name, q.customer_name, q.status].filter(Boolean).join(" · ") || "Draft";
                                    return (
                                        <ListItemButton
                                            key={q.id}
                                            selected={selectedId === q.id}
                                            onClick={() => handleSelectQuote(q)}
                                        >
                                            <ListItemText
                                                slotProps={{
                                                    primary: {
                                                        sx: {
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                        },
                                                    },
                                                }}
                                                primary={q.title}
                                                secondary={sub}
                                            />
                                        </ListItemButton>
                                    );
                                })
                            )}
                        </List>
                    </Paper>

                    <Paper elevation={0} variant="outlined" sx={{ flex: 1, minWidth: 0, p: { xs: 2, sm: 2.5 } }}>
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1}
                            sx={{ mb: 2 }}
                            alignItems={{ sm: "center" }}
                            justifyContent="space-between"
                        >
                            <Typography variant="subtitle1" fontWeight={700}>
                                {selectedId ? "Edit worksheet" : "New worksheet"}
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Tooltip title="Copy plain-text outline for estimates or RFQs">
                                    <span>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<ContentCopyIcon />}
                                            onClick={handleCopy}
                                        >
                                            Copy summary
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                >
                                    Save
                                </Button>
                                {selectedId ? (
                                    <IconButton color="error" aria-label="Delete" onClick={() => setDeleteOpen(true)}>
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                ) : null}
                            </Stack>
                        </Stack>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    label="Quote / job title"
                                    fullWidth
                                    required
                                    value={form.title}
                                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <InputLabel id="status-label">Pipeline status</InputLabel>
                                    <Select
                                        labelId="status-label"
                                        label="Pipeline status"
                                        value={form.status}
                                        onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                                    >
                                        {STATUS_OPTIONS.map((o) => (
                                            <MenuItem key={o.value} value={o.value}>
                                                {o.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="customer-label">Customer</InputLabel>
                                    <Select
                                        labelId="customer-label"
                                        label="Customer"
                                        value={form.customer_id || ""}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                customer_id: e.target.value,
                                                project_id: "",
                                            }))
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>Select customer…</em>
                                        </MenuItem>
                                        {customers.map((c) => (
                                            <MenuItem key={c.id} value={c.id}>
                                                {c.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="project-label">Project (estimate baseline)</InputLabel>
                                        <Select
                                            labelId="project-label"
                                            label="Project (estimate baseline)"
                                            value={form.project_id || ""}
                                            disabled={!form.customer_id}
                                            onChange={(e) => setForm((f) => ({ ...f, project_id: e.target.value }))}
                                        >
                                            <MenuItem value="">
                                                <em>No project — standalone quote</em>
                                            </MenuItem>
                                            {customerProjectsFlat.map((p) => (
                                                <MenuItem key={p.id} value={p.id}>
                                                    {p.depth ? `${"— ".repeat(p.depth)}${p.name}` : p.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        disabled={!form.customer_id}
                                        onClick={() => {
                                            setNewProjectForm((f) => ({
                                                ...f,
                                                customer_id: form.customer_id,
                                                parent_id: "",
                                            }));
                                            setProjectDialogOpen(true);
                                        }}
                                        sx={{ whiteSpace: "nowrap", minWidth: 120 }}
                                    >
                                        New project
                                    </Button>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={!form.project_id}
                                        aria-label="Delete selected project"
                                        onClick={() => setDeleteProjectOpen(true)}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Stack>
                                {projectNameForForm ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                        Linked to project: {projectNameForForm}
                                    </Typography>
                                ) : wixProjectName ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                        Website planner scope: {wixProjectName}
                                    </Typography>
                                ) : null}
                            </Grid>

                            <Grid item xs={12}>
                                <Tabs
                                    value={worksheetTab}
                                    onChange={(_, v) => setWorksheetTab(v)}
                                    variant="fullWidth"
                                    sx={{ borderBottom: 1, borderColor: "divider" }}
                                >
                                    <Tab label="Planner" />
                                    <Tab label="Scope worksheet" />
                                    <Tab label="Pricing & estimate" />
                                </Tabs>

                                <WorksheetTabPanel value={worksheetTab} index={0}>
                                    <QuotePlannerPanel
                                        plannerGroups={plannerGroups}
                                        setPlannerGroups={setPlannerGroups}
                                        groupDefinitions={groupDefinitions}
                                        onRefreshDefinitions={fetchGroupDefinitions}
                                        estimateLines={estimateLines}
                                        setEstimateLines={setEstimateLines}
                                        rates={pricingModel?.rates || []}
                                        currency={pricingSettings.currency || "USD"}
                                        onAddEstimateLineForGroup={handleAddEstimateLineForGroup}
                                        planTitle={form.title || projectNameForForm || "Quote plan"}
                                    />
                                </WorksheetTabPanel>

                                <WorksheetTabPanel value={worksheetTab} index={1}>
                                    <QuoteScopeSections
                                        form={form}
                                        setForm={setForm}
                                        sectionFlags={sectionFlags}
                                        setSectionFlags={setSectionFlags}
                                    />
                                </WorksheetTabPanel>

                                <WorksheetTabPanel value={worksheetTab} index={2}>
                                    {pricingLoading ? (
                                        <Alert severity="info">Loading pricing playbook…</Alert>
                                    ) : !pricingModel ? (
                                        <Alert severity="warning">
                                            Pricing catalog unavailable — confirm the backend is running (`/pricing`).
                                        </Alert>
                                    ) : (
                                        <QuoteEstimatePanel
                                            currency={pricingSettings.currency || "USD"}
                                            settings={pricingSettings}
                                            rates={pricingModel.rates || []}
                                            presets={pricingModel.presets || []}
                                            estimateLines={estimateLines}
                                            setEstimateLines={setEstimateLines}
                                            materialCost={coerceMoneyNumber(form.material_cost_at_purchase)}
                                            onMaterialCostChange={(n) =>
                                                setForm((f) => ({ ...f, material_cost_at_purchase: n }))
                                            }
                                            customerQuoteOverride={form.customer_quote_override}
                                            onCustomerQuoteOverrideChange={(s) =>
                                                setForm((f) => ({ ...f, customer_quote_override: s }))
                                            }
                                            onCopyCustomerBrief={handleCopyCustomerBriefClipboard}
                                            onCopyCostBreakdown={handleCopyCostDetailClipboard}
                                            onExportInternalPdf={handleExportInternalPdf}
                                            onExportCustomerPdf={handleExportCustomerPdf}
                                            pdfExporting={pdfExporting}
                                            plannerGroups={plannerGroups}
                                            wixComponents={wixComponents}
                                            wixJob={wixJob}
                                            activeGroupFilter={estimateGroupFilter}
                                            onActiveGroupFilterChange={setEstimateGroupFilter}
                                        />
                                    )}
                                </WorksheetTabPanel>
                            </Grid>
                        </Grid>
                    </Paper>
                </Stack>
            </Stack>

            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
                <DialogTitle>Delete worksheet?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This removes the saved quote worksheet from your database. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteConfirmed}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteProjectOpen} onClose={() => setDeleteProjectOpen(false)}>
                <DialogTitle>Delete project?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Remove <strong>{projectNameForForm || "this project"}</strong> from the database. Linked quotes
                        stay saved but will no longer be tied to this project.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteProjectOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDeleteProjectConfirmed}>
                        Delete project
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={projectDialogOpen} onClose={() => setProjectDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New project</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ pt: 0.5 }}>
                        <FormControl fullWidth>
                            <InputLabel id="parent-project-label">Parent project (optional)</InputLabel>
                            <Select
                                labelId="parent-project-label"
                                label="Parent project (optional)"
                                value={newProjectForm.parent_id || ""}
                                onChange={(e) => setNewProjectForm((f) => ({ ...f, parent_id: e.target.value }))}
                            >
                                <MenuItem value="">
                                    <em>Top-level project</em>
                                </MenuItem>
                                {customerProjectsFlat.map((p) => (
                                    <MenuItem key={p.id} value={p.id}>
                                        {p.depth ? `${"— ".repeat(p.depth)}${p.name}` : p.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Project name"
                            fullWidth
                            required
                            value={newProjectForm.name}
                            onChange={(e) => setNewProjectForm((f) => ({ ...f, name: e.target.value }))}
                        />
                        <TextField
                            label="Site / location"
                            fullWidth
                            value={newProjectForm.site_location}
                            onChange={(e) => setNewProjectForm((f) => ({ ...f, site_location: e.target.value }))}
                        />
                        <TextField
                            label="Description"
                            fullWidth
                            multiline
                            minRows={2}
                            value={newProjectForm.description}
                            onChange={(e) => setNewProjectForm((f) => ({ ...f, description: e.target.value }))}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProjectDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateProject}>
                        Create project
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Quotes;
